import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { DATA_REFRESH_EVENT } from '../../contexts/WebSocketContext';
import { 
  FileText, Clock, CheckCircle, AlertCircle, Download, User, 
  ChevronLeft, Award, Calendar, File, FileImage, FileArchive,
  Save, X, Eye, RefreshCcw, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const TrainerHomeworkSubmissionsPage = ({ onMenuClick }) => {
  const { homeworkId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  const [gradeScore, setGradeScore] = useState('');
  const [gradeFeedback, setGradeFeedback] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const response = await api.get(`/homework/${homeworkId}/submissions`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Erreur lors du chargement des soumissions');
    } finally {
      setLoading(false);
    }
  }, [homeworkId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Listen for real-time updates
  useEffect(() => {
    const handleDataRefresh = (event) => {
      const { type, homework_id } = event.detail;
      if (type === 'homework_submitted' && homework_id === parseInt(homeworkId)) {
        fetchData();
        toast.info('Nouvelle soumission reçue');
      }
    };

    window.addEventListener(DATA_REFRESH_EVENT, handleDataRefresh);
    return () => window.removeEventListener(DATA_REFRESH_EVENT, handleDataRefresh);
  }, [fetchData, homeworkId]);

  const handleGrade = async () => {
    if (!selectedSubmission) return;
    
    const score = parseFloat(gradeScore);
    if (isNaN(score) || score < 0 || score > (data?.homework?.max_score || 100)) {
      toast.error(`Note invalide (0 - ${data?.homework?.max_score || 100})`);
      return;
    }

    setSaving(true);
    try {
      await api.put(`/homework-submissions/${selectedSubmission.id}/grade`, {
        score: score,
        feedback: gradeFeedback
      });
      
      toast.success('Note enregistrée');
      setGradeDialogOpen(false);
      setSelectedSubmission(null);
      setGradeScore('');
      setGradeFeedback('');
      fetchData();
    } catch (error) {
      console.error('Error grading:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const openGradeDialog = (submission) => {
    setSelectedSubmission(submission);
    setGradeScore(submission.score?.toString() || '');
    setGradeFeedback(submission.feedback || '');
    setGradeDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { color: 'bg-amber-100 text-amber-700', icon: Clock, text: 'En attente' },
      submitted: { color: 'bg-blue-100 text-blue-700', icon: Clock, text: 'Soumis' },
      graded: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, text: 'Corrigé' }
    };
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.text}
      </Badge>
    );
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return FileImage;
    if (['zip', 'rar'].includes(ext)) return FileArchive;
    return File;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  const homework = data?.homework;
  const submissions = data?.submissions || [];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Correction des devoirs" onMenuClick={onMenuClick} />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back button */}
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        {/* Homework info */}
        {homework && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">{homework.title}</CardTitle>
                  <CardDescription className="mt-1">{homework.description}</CardDescription>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Échéance: {formatDate(homework.due_date)}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Note max: {homework.max_score || 100} pts
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Fichiers joints au devoir */}
              {homework.attached_files && homework.attached_files.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-2">Fichiers fournis aux étudiants :</p>
                  <div className="flex flex-wrap gap-2">
                    {homework.attached_files.map((file, idx) => {
                      const FileIcon = getFileIcon(file.name);
                      return (
                        <a
                          key={idx}
                          href={`${process.env.REACT_APP_BACKEND_URL}/api${file.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs hover:bg-blue-100"
                        >
                          <FileIcon className="w-3 h-3" />
                          <Download className="w-3 h-3" />
                          {file.name}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-900 dark:text-white">{data?.total || 0}</p>
              <p className="text-sm text-slate-500">Soumissions</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-600">{data?.pending || 0}</p>
              <p className="text-sm text-slate-500">À corriger</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600">{data?.graded || 0}</p>
              <p className="text-sm text-slate-500">Corrigés</p>
            </CardContent>
          </Card>
        </div>

        {/* Submissions list */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Travaux rendus</CardTitle>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCcw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {submissions.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Aucune soumission pour le moment</p>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="space-y-4">
                  {submissions.map((sub) => (
                    <div
                      key={sub.id}
                      className={`p-4 rounded-lg border transition-all ${
                        sub.status === 'graded' 
                          ? 'bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200'
                          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                      }`}
                      data-testid={`submission-${sub.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          {/* Student info */}
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-medium">
                              {sub.student_name.charAt(0)}
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-900 dark:text-white">
                                {sub.student_name}
                              </h4>
                              <p className="text-xs text-slate-500">{sub.student_email}</p>
                            </div>
                            {getStatusBadge(sub.status)}
                            {sub.score !== null && (
                              <Badge className="bg-violet-100 text-violet-700">
                                <Award className="w-3 h-3 mr-1" />
                                {sub.score}/{homework?.max_score || 100}
                              </Badge>
                            )}
                          </div>

                          {/* Content */}
                          {sub.content && (
                            <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg mb-3">
                              <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                                {sub.content}
                              </p>
                            </div>
                          )}

                          {/* Fichiers soumis */}
                          {sub.attached_files && sub.attached_files.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs font-medium text-slate-600 mb-2">Fichiers rendus :</p>
                              <div className="flex flex-wrap gap-2">
                                {sub.attached_files.map((file, idx) => {
                                  const FileIcon = getFileIcon(file.name);
                                  return (
                                    <a
                                      key={idx}
                                      href={`${process.env.REACT_APP_BACKEND_URL}/api${file.url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-md text-sm border border-emerald-200 hover:bg-emerald-100 transition-colors"
                                    >
                                      <FileIcon className="w-4 h-4" />
                                      <Download className="w-4 h-4" />
                                      {file.name}
                                    </a>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Feedback existant */}
                          {sub.feedback && (
                            <div className="p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200">
                              <p className="text-xs font-medium text-violet-700 mb-1">Mon commentaire :</p>
                              <p className="text-sm text-slate-700">{sub.feedback}</p>
                            </div>
                          )}

                          {/* Dates */}
                          <div className="flex items-center gap-4 text-xs text-slate-500 mt-2">
                            <span>Soumis: {formatDate(sub.submitted_at)}</span>
                            {sub.graded_at && <span>Corrigé: {formatDate(sub.graded_at)}</span>}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                          <Button 
                            size="sm"
                            onClick={() => openGradeDialog(sub)}
                            data-testid={`grade-btn-${sub.id}`}
                          >
                            {sub.status === 'graded' ? (
                              <>
                                <Eye className="w-4 h-4 mr-1" />
                                Modifier
                              </>
                            ) : (
                              <>
                                <Award className="w-4 h-4 mr-1" />
                                Corriger
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Grade Dialog */}
      <Dialog open={gradeDialogOpen} onOpenChange={setGradeDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedSubmission?.status === 'graded' ? 'Modifier la correction' : 'Corriger le devoir'}
            </DialogTitle>
            <DialogDescription>
              {selectedSubmission?.student_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Score */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Note (sur {homework?.max_score || 100})
              </label>
              <Input
                type="number"
                value={gradeScore}
                onChange={(e) => setGradeScore(e.target.value)}
                min="0"
                max={homework?.max_score || 100}
                placeholder="0"
                data-testid="grade-score-input"
              />
            </div>

            {/* Feedback */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Commentaire
              </label>
              <Textarea
                value={gradeFeedback}
                onChange={(e) => setGradeFeedback(e.target.value)}
                placeholder="Commentaire sur le travail de l'étudiant..."
                rows={4}
                data-testid="grade-feedback-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setGradeDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleGrade} disabled={saving} data-testid="save-grade-btn">
              {saving ? 'Enregistrement...' : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainerHomeworkSubmissionsPage;
